<template>
  <section class="bg-white border border-slate-100 rounded-2xl shadow-sm p-6">
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-2">
        <div class="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center">
          <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        </div>
        <h2 class="text-base font-bold text-slate-800">Saved Locations</h2>
        <span v-if="locations.length" class="text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
          {{ locations.length }}
        </span>
      </div>

      <button
        @click="$emit('refresh')"
        class="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 px-2.5 py-1.5 rounded-lg transition-colors"
      >
        <svg class="w-3.5 h-3.5" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        Refresh
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-10">
      <svg class="w-5 h-5 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
    </div>

    <!-- Empty state -->
    <div v-else-if="locations.length === 0" class="flex flex-col items-center justify-center py-10 text-center">
      <div class="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
        <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
      </div>
      <p class="text-sm font-medium text-slate-600">No saved locations</p>
      <p class="text-xs text-slate-400 mt-1">Add your favourite pickup spots using the form.</p>
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="location in locations"
        :key="location._id"
        class="border border-slate-100 rounded-xl p-4 hover:border-slate-200 transition-colors"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <div class="w-5 h-5 bg-emerald-100 rounded flex items-center justify-center flex-shrink-0">
                <svg class="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
              <p class="text-sm font-semibold text-slate-800">{{ location.label }}</p>
            </div>
            <p class="text-xs text-slate-500 ml-7">{{ location.address }}, {{ location.city }}, {{ location.country }}</p>
          </div>

          <div class="flex items-center gap-2 flex-shrink-0">
            <button
              @click="$emit('weather', location._id)"
              class="flex items-center gap-1.5 bg-sky-50 text-sky-600 border border-sky-200 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-sky-100 transition-colors"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/>
              </svg>
              Weather
            </button>
            <button
              @click="$emit('edit', location)"
              class="flex items-center gap-1 text-slate-400 hover:text-emerald-600 border border-slate-200 hover:border-emerald-200 hover:bg-emerald-50 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
              title="Edit location"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              Edit
            </button>
            <button
              @click="$emit('delete', location._id)"
              class="flex items-center gap-1 text-slate-400 hover:text-red-500 border border-slate-200 hover:border-red-200 hover:bg-red-50 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
              title="Remove location"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>

    <WeatherCard :weather="weather" />
  </section>
</template>

<script>
import WeatherCard from "@/components/WeatherCard.vue";

export default {
  name: "LocationList",
  components: { WeatherCard },
  emits: ["refresh", "weather", "delete", "edit"],
  props: {
    locations: { type: Array, required: true },
    weather:   { type: Object, default: null },
    loading:   { type: Boolean, default: false },
  },
};
</script>
